/**
 * 能力探测的 React 入口。
 *
 * 探测只在挂载时做一次并缓存：`collectRuntimeFacts()` 会创建 WebGL 上下文，
 * 每次渲染都探一次既浪费也无必要（运行期硬件不会变）。
 * 唯一会变的是用户手动选的画质档位，所以那部分单独放 state。
 */
import { useEffect, useMemo, useState } from 'react';

import { buildGuidance, type Guidance } from './browserGuide';
import {
  engineLabel,
  probeCapability,
  type CapabilityReport,
  type QualityTier,
} from './capability';
import { decideGate, type GateDecision } from './directorGate';
import { getQualityPreset, resolvePreset, type QualityPreset } from './qualityPresets';

export interface CapabilityView {
  /** null 表示探测尚未完成（首帧） */
  report: CapabilityReport | null;
  engine: string;
  guidance: Guidance | null;
  /** 导演台入口的处置：enter / block / blockWithForce。D-3 的唯一依据 */
  gate: GateDecision | null;
  /** 当前生效画质（已按 D-2 规则被上限收敛过） */
  preset: QualityPreset | null;
  /** 用户请求但被弹回的原档位；null 表示没被弹回 */
  bounced: QualityTier | null;
  /** 用户手动请求档位（高于探测档位会被 resolvePreset 弹回） */
  requestTier: (tier: QualityTier | null) => void;
}

export function useCapability(): CapabilityView {
  const [report, setReport] = useState<CapabilityReport | null>(null);
  const [engine, setEngine] = useState('');
  const [requested, setRequested] = useState<QualityTier | null>(null);

  useEffect(() => {
    setEngine(engineLabel());
    setReport(probeCapability());
  }, []);

  const guidance = useMemo(
    () => (report === null ? null : buildGuidance(report, engine)),
    [report, engine],
  );

  const gate = useMemo(
    () => (report !== null && guidance !== null ? decideGate(report, guidance) : null),
    [report, guidance],
  );

  // 纯派生，切勿在渲染期 setState
  const preset = report === null ? null : resolvePreset(report.tier, requested);
  const bounced = requested !== null && preset !== null && preset.tier !== requested
    ? requested
    : null;

  return {
    report,
    engine,
    guidance,
    gate,
    preset: preset ?? getQualityPreset('low'),
    bounced,
    requestTier: setRequested,
  };
}
